import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function OnboardingStatus() {
  const { user } = useAuth();
  if (user.role !== "customer") return <StaffQueue />;
  return <MyApplications />;
}

function StaffQueue() {
  const [apps, setApps] = useState(null);
  useEffect(() => { api.get("/compliance/queue").then((r) => setApps(r.data)).catch(() => setApps([])); }, []);
  if (!apps) return <Spinner />;
  return (
    <div>
      <PageHeader overline="Onboarding" title="Onboarding Status"
        subtitle="Applications in progress / submitted. Customers complete the guided KYC journeys (Individual / Corporate) from the Onboarding page."
        testid="onboarding-header" />
      <Card className="p-5">
        {apps.length === 0 ? <EmptyState title="No submitted applications" hint="Sign in as the Customer demo account to complete a journey." /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Applicant</TableHead><TableHead>Type</TableHead><TableHead>Step</TableHead><TableHead>Status</TableHead><TableHead>Risk</TableHead></TableRow></TableHeader>
            <TableBody>{apps.map((a) => (
              <TableRow key={a.id}><TableCell>{a.applicant_name}</TableCell><TableCell className="capitalize">{a.kyc_type || "individual"}</TableCell><TableCell className="capitalize">{a.current_step}</TableCell>
                <TableCell><StatusBadge status={a.status} /></TableCell><TableCell>{a.compliance_risk_rating?.rating || "—"}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function MyApplications() {
  const [apps, setApps] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/onboarding/mine").then((r) => setApps(r.data)).catch(() => setApps([])); }, []);
  if (!apps) return <Spinner />;
  const openJourney = (a) => navigate(`/corporate-onboarding?tab=${(a.kyc_type || "individual") === "corporate" ? "corporate" : "individual"}`);
  const inProgress = (a) => ["draft", "information_requested"].includes(a.status);
  return (
    <div>
      <PageHeader overline="Onboarding" title="Onboarding Status"
        subtitle="Your KYC applications and their review status. Start or resume a journey from the Onboarding page."
        testid="onboarding-status-header" />
      <Card className="p-5" data-testid="onboarding-status-page">
        {apps.length === 0 ? (
          <div className="text-center py-4">
            <EmptyState title="No applications yet" hint="Start your Individual or Corporate KYC journey from the Onboarding page." />
            <Button className="mt-3" onClick={() => navigate("/corporate-onboarding")} data-testid="start-onboarding-btn">Start onboarding</Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Step</TableHead><TableHead>Status</TableHead><TableHead>Risk</TableHead><TableHead>Updated</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>{apps.map((a) => (
                <TableRow key={a.id} data-testid={`my-app-${a.id}`}>
                  <TableCell className="capitalize">{a.kyc_type || "individual"}</TableCell>
                  <TableCell className="capitalize">{inProgress(a) ? (a.current_step || "—") : "—"}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                  <TableCell>{a.compliance_risk_rating?.rating || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-num">{(a.updated_at || a.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell><Button size="sm" variant="outline" onClick={() => openJourney(a)} data-testid={`open-app-${a.id}`}>{inProgress(a) ? "Resume" : "View"}</Button></TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
